/**
 * Sprint M5.39 (30.07.2026) — Block A3.
 * Sprint M5.40 (30.07.2026) — Block D1: 5-Stufen-Value-Ladder statt 3.
 * Sprint M5.44 (31.07.2026) — Free-Cockpit-UI-Drift:
 *   - „Free" → „Schnupperplan" in sichtbarer Copy
 *   - Bug „Pro · Pro" (ProPro) behoben — der Plan-Tag steht nur einmal
 *   - Interne Sprint-IDs (M-22/M-23) + „Drip-In" aus Detail-Text entfernt
 *   - Schritt 4 + 5 benefit-orientiert formuliert
 *   - Abschluss-Text ersetzt Metapher „Tür/Wellenlaenge" durch klaren
 *     Weg-zu-Pro-Solo-Satz
 *
 * „Wie geht es weiter?" — dezenter Big-Picture-Conversion-Anreiz.
 * Zeigt 5 Schritte, jeder mit klarem Plan-Bezug. Free-User sehen Schritt 1+2
 * als „Du bist hier", Schritt 3–5 als Pro-only. Pro-User sehen entsprechend
 * verschobene Marker.
 *
 * Regel: KEIN Preisschild. KEIN „Jetzt upgraden"-Button. KEINE Feature-Vergleichsliste.
 */
import React from "react";
import { Check } from "lucide-react";

const STEPS = [
  {
    id: 1,
    plan: "Schnupperplan",
    label: "Du entdeckst deinen Fokustyp.",
    detail: "Quick-Check mit 5 Fragen und ein erster Blick auf die anderen drei Fokustypen.",
  },
  {
    id: 2,
    plan: "Schnupperplan",
    label: "Du lernst die anderen kennen.",
    detail: "Fokustypen-Videokurs mit 5 kurzen Videos — du siehst, wie die anderen ticken.",
  },
  {
    id: 3,
    plan: "Pro Solo",
    label: "Du verstehst die anderen.",
    detail: "Vertiefte Steckbriefe und ein Detail-Test, der dir zeigt, was hinter den Mustern steckt.",
  },
  {
    id: 4,
    plan: "Pro Solo",
    label: "Du gestaltest Situationen bewusster.",
    detail: "Du entwickelst deinen eigenen Umgang mit unterschiedlichen Mustern — im Alltag, in Beziehungen, im Beruf.",
  },
  {
    id: 5,
    plan: "Pro Solo",
    label: "Du erhältst regelmäßige Empfehlungen für Alltag und Beziehungen.",
    detail: "Ein lernendes System, das die Muster mitliest, die du immer besser verstehst — und dir passgenaue Impulse gibt.",
  },
];

// Sprint M5.40 (30.07.2026) — Block D1.
// `isPro` steuert, welche Schritte als „done" markiert werden.
// Free: 1+2 = „Du bist hier". Pro: 1+2 = „Done", 3 = „Du bist hier" (kann noch
// M-11 laufen, Sektionen sind offen), 4+5 = in Arbeit / offen.
export default function WieGehtEsWeiter({ isPro = false }) {
  return (
    <section
      data-testid="wie-geht-es-weiter"
      className="mt-8 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 sm:p-8"
    >
      <p className="text-xs font-bold uppercase tracking-widest text-slate-500">
        Wie geht es weiter?
      </p>
      <h2 className="mt-1 text-lg sm:text-xl font-extrabold text-slate-900">
        Dein Weg — in 5 Schritten
      </h2>
      <ol className="mt-5 space-y-3">
        {STEPS.map((step) => {
          // Aktuelle Position:
          //  Free: 1+2 „hier", 3–5 als Pro-Solo-Ausbaustufe
          //  Pro:  1+2 „done", 3 „hier", 4+5 „offen"
          const isFreeStep = step.plan === "Schnupperplan";
          let state;
          if (!isPro) {
            state = isFreeStep ? "here" : "pro_locked";
          } else if (step.id <= 2) {
            state = "done";
          } else if (step.id === 3) {
            state = "here";
          } else {
            state = "open";
          }

          // Sprint M5.44 Fix: Kein doppeltes „Pro · Pro"-Rendering mehr.
          // Der Plan-Name ist die einzige Quelle, `badgeLabel` beschreibt
          // nur den Zustand („Du bist hier" / „Done" / „Verfügbar in Pro Solo" / leer).
          const stateConfig = {
            here: {
              circle: "bg-emerald-100 text-emerald-700",
              badge: "text-emerald-700",
              badgeLabel: "Du bist hier",
              titleColor: "text-slate-900",
              icon: null,
            },
            done: {
              circle: "bg-emerald-500 text-white",
              badge: "text-emerald-700",
              badgeLabel: "Erledigt",
              titleColor: "text-slate-900",
              icon: <Check className="w-3.5 h-3.5" />,
            },
            open: {
              circle: "bg-blue-100 text-[#4563BB]",
              badge: "text-[#4563BB]",
              badgeLabel: "",
              titleColor: "text-slate-900",
              icon: null,
            },
            pro_locked: {
              circle: "bg-slate-100 text-slate-400",
              badge: "text-slate-500",
              badgeLabel: "Verfügbar in Pro Solo",
              titleColor: "text-slate-700",
              icon: null,
            },
          }[state];

          const showStateLabel = stateConfig.badgeLabel && state !== "pro_locked";
          // pro_locked ersetzt den Plan-Namen direkt (statt „Pro Solo · Verfügbar in Pro Solo"
          // schreiben wir nur „Verfügbar in Pro Solo").
          const headerLabel =
            state === "pro_locked" ? stateConfig.badgeLabel : step.plan;

          return (
            <li
              key={step.id}
              data-testid={`wgew-schritt-${step.id}`}
              data-state={state}
              data-plan={step.plan}
              className="flex gap-3 items-start"
            >
              <span
                className={`w-7 h-7 flex-shrink-0 rounded-full font-bold text-xs flex items-center justify-center ${stateConfig.circle}`}
              >
                {stateConfig.icon || step.id}
              </span>
              <div className="flex-1">
                <p className={`text-sm font-bold ${stateConfig.titleColor}`}>
                  Schritt {step.id}{" "}
                  <span className={stateConfig.badge}>
                    · {headerLabel}
                    {showStateLabel && <> · {stateConfig.badgeLabel}</>}
                  </span>
                </p>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {step.label}
                </p>
                <p className="text-xs text-slate-500 leading-snug mt-0.5">
                  {step.detail}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
      {/* Sprint M5.44 (31.07.2026) — Block D:
          Abschluss-Text spricht Free-User explizit an und fuehrt sanft
          zu Pro Solo weiter. Fuer Pro-User zeigen wir einen bestaetigenden
          Weg-fortgesetzt-Satz. */}
      <p
        className="mt-6 text-sm text-slate-700 leading-relaxed"
        data-testid="wgew-abschluss"
      >
        {isPro ? (
          <>
            Du bist mitten auf deinem Weg — die Grundlagen sitzen, jetzt
            geht es darum, Situationen bewusster zu gestalten und Muster
            immer klarer zu erkennen.
          </>
        ) : (
          <>
            Du hast hier die Grundlagen gelegt. Wenn du den vollständigen
            Weg und den gesamten Mehrwert nutzen möchtest, findest du ihn
            in Pro Solo.
          </>
        )}
      </p>
    </section>
  );
}
